import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import uuid4 from 'uuid/v4';
import urlJoin from 'url-join';
import { normalizeMediaObject } from 'api';
import { defaultMapStyle } from 'app-constants';
import { useWrapperContext, PinMap } from 'msp-components';
import { useAnalytics } from 'hooks';
import { Route, Switch } from 'react-router-dom';
import ResizeDetector from 'react-resize-detector';
import Error404 from 'components/Error404';

const normalizeMarkerTypeData = items => items.map(item => ({
  id: item.uid,
  label: item.name,
  icon: item.icon,
  color: undefined,
  size: item.size,
  inLegend: item.inLegend,
}));

const normalizeCategoryData = items => items.map(item => ({
  id: uuid4(),
  label: item,
}));

const normalizeMapItemData = (items, imgWidth = 1500, categories) => items.reduce((result, item) => {
  const media = item.media.map(media => normalizeMediaObject(media, imgWidth));
  const categoryIds = item.categories.map(c => categories.find(({ label }) => label === c).id);

  const itemObj = {
    id: item.uid,
    slug: item.slug,
    title: item.name,
    description: item.description,
    locationType: item.locationType,
    latitude: item.latitude,
    longitude: item.longitude,
    countries: item.countries,
    markerTypeId: item.marker,
    categoryIds,
    media,
  };

  result.byId[item.uid] = itemObj;
  result.bySlug[item.slug] = itemObj;
  result.ordered.push(itemObj);
  return result;
}, { bySlug: {}, byId: {}, ordered: [] });

const PinMapTitle = ({
  routeProps,
  canonicalPath,
  mapStyle,
  defaultLatitude,
  defaultLongitude,
  defaultZoom,
  mapboxToken,
  displayOptions,
  mapItems,
  markerTypes,
  categories,
}) => {
  const [containerWidth, setContainerWidth] = useState();
  const handleResize = width => setContainerWidth(width);

  const { containerWidth: wrapperWidth, toggleMenu } = useWrapperContext();

  const { match, history } = routeProps;
  const titleBasePath = canonicalPath || match.url;

  const { itemsList, legend } = displayOptions.menuItems;

  const mapTitleCategories = useMemo(() => normalizeCategoryData(categories), [categories]);
  const mapTitleMarkerTypes = useMemo(() => normalizeMarkerTypeData(markerTypes), [markerTypes]);
  const mapTitleItems = useMemo(() => (
    normalizeMapItemData(mapItems, containerWidth && containerWidth * 1.5, mapTitleCategories)
  ), [mapItems, containerWidth, mapTitleCategories]);

  const handleItemSelect = id => {
    if (id) {
      const slug = mapTitleItems.byId[id].slug;
      history.push(urlJoin(titleBasePath, slug));
      // On small screens, toggle the menu off when a map item is selected.
      if (wrapperWidth < 750) toggleMenu(false);
    } else {
      history.push(titleBasePath);
    }
  };

  const { trackMediaPlay, trackMediaProgress } = useAnalytics();

  let defaultViewport;
  if ([defaultLatitude, defaultLongitude, defaultZoom].every(val => typeof val === 'number')) {
    defaultViewport = {
      latitude: defaultLatitude,
      longitude: defaultLongitude,
      zoom: defaultZoom,
    };
  }

  return (
    <Switch>
      <Route
        exact
        path={urlJoin(match.url, ':itemSlug?')}
        render={props => {
          // Validate slug
          let activeItemId = null;
          // eslint-disable-next-line
          const { itemSlug } = props.match.params;
          if (itemSlug) {
            const activeItem = mapTitleItems.bySlug[itemSlug];
            if (typeof activeItem !== 'undefined') {
              activeItemId = activeItem.id;
            } else {
              return <Error404 />;
            }
          }

          return (
            <>
              <ResizeDetector
                handleWidth
                onResize={handleResize}
                refreshMode="debounce"
                refreshRate={500}
              />
              <PinMap
                mapboxToken={mapboxToken}
                mapStyle={mapStyle || defaultMapStyle}
                hideLegend={!legend}
                hideItemsList={!itemsList}
                defaultViewport={defaultViewport}
                markerTypes={mapTitleMarkerTypes}
                categories={mapTitleCategories}
                items={mapTitleItems.ordered}
                activeItemId={activeItemId}
                onSelectItem={handleItemSelect}
                onMediaPlay={trackMediaPlay}
                onMediaProgress={trackMediaProgress}
              />
            </>
          );
        }}
      />
      <Route component={Error404} />
    </Switch>
  );
};

PinMapTitle.propTypes = {
  routeProps: PropTypes.object,
  canonicalPath: PropTypes.string,
  mapStyle: PropTypes.string,
  defaultLatitude: PropTypes.number,
  defaultLongitude: PropTypes.number,
  defaultZoom: PropTypes.number,
  mapboxToken: PropTypes.string,
  displayOptions: PropTypes.shape({
    menuItems: PropTypes.shape({
      legend: PropTypes.bool,
      itemsList: PropTypes.bool,
    }),
  }),
  mapItems: PropTypes.arrayOf(PropTypes.object),
  markerTypes: PropTypes.arrayOf(PropTypes.object),
  categories: PropTypes.arrayOf(PropTypes.string),
};

export default PinMapTitle;
